import { useRef } from "react";
import {
  IconBold,
  IconItalic,
  IconStrikethrough,
  IconPaperclip,
  IconPhoto,
  IconFile,
  IconX,
} from "@tabler/icons-react";
import { useIonToast } from "@ionic/react";
import { useIntl } from "react-intl";

import styles from "./styles.module.css";

export const DEFAULT_META_FILES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
];
export const MAX_BYTES = 10 * 1024 * 1024;

type TemplateInputProps = {
  rows?: number;
  value?: string;
  variables: string[];
  disabled?: boolean;
  error?: boolean;
  maxLength?: number;
  id?: string;
  disableBreakLine?: boolean;
  allowFiles?: boolean;
  file?: File | null;
  setFile?: (file: File | null) => void;
  setText: (text: string) => void;
};

const TemplateInput = ({
  rows,
  value,
  maxLength = 940,
  variables,
  disabled = false,
  error = false,
  id,
  disableBreakLine = false,
  allowFiles = false,
  file,
  setFile,
  setText,
}: TemplateInputProps) => {
  const text = value?.toString() || "";

  const localRef = useRef<HTMLTextAreaElement>(null);

  const intl = useIntl();
  const [present] = useIonToast();

  const handleInsertText = (insertText: string) => {
    if (localRef.current) {
      const startPos = localRef.current.selectionStart;
      const endPos = localRef.current.selectionEnd;
      const beforeInsert = text.substring(0, startPos);
      const afterInsert = text.substring(endPos, text.length);

      const textToInsert = beforeInsert + insertText + afterInsert;
      setText(textToInsert);

      const insertTextLength = insertText.length;

      setTimeout(() => {
        if (localRef.current) {
          localRef.current.selectionStart = localRef.current.selectionEnd =
            startPos + insertTextLength;
          localRef.current.focus();
        }
      }, 0);
    }
  };

  const applyTextStyle = (style: string) => {
    if (localRef.current) {
      const textarea = localRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      if (start !== end) {
        const selectedText = text.substring(start, end);
        const newText =
          text.substring(0, start) +
          `${style}${selectedText}${style}` +
          text.substring(end);
        setText(newText);

        setTimeout(() => {
          textarea.setSelectionRange(start, end + 2);
          textarea.focus();
        }, 0);
      }
    }
  };

  const onPreventBreakLine = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files?.[0];
    if (
      inputFile &&
      setFile &&
      DEFAULT_META_FILES.includes(inputFile.type) &&
      inputFile.size <= MAX_BYTES
    ) {
      return setFile(inputFile);
    }
    event.target.value = "";
    return present({
      message: intl.formatMessage({ id: "newConv.invalidFile" }),
      duration: 3500,
      color: "light",
      position: "top",
      cssClass: "error-toast",
    });
  };

  return (
    <div
      className={`${styles.container} ${
        disabled ? styles.textareaDisabled : ""
      } ${error ? styles.textareaError : ""}`}
    >
      <textarea
        ref={localRef}
        id={id}
        value={text}
        rows={rows || 8}
        className={`${styles.textarea} form-control`}
        onChange={(e) => setText(e.target.value)}
        maxLength={maxLength}
        disabled={disabled}
        required
        {...(disableBreakLine ? { onKeyDown: onPreventBreakLine } : {})}
      />
      {!disabled && (
        <div className={styles.actions}>
          <div className={styles.variablesContainer}>
            <IconBold
              size={18}
              role="button"
              onClick={() => applyTextStyle("*")}
              color="var(--ion-color-step-600)"
            />
            <IconItalic
              size={18}
              role="button"
              onClick={() => applyTextStyle("_")}
              color="var(--ion-color-step-600)"
            />
            <IconStrikethrough
              size={19}
              role="button"
              onClick={() => applyTextStyle("~")}
              color="var(--ion-color-step-600)"
              style={{ marginRight: allowFiles ? "0" : "0.8rem" }}
            />
            {allowFiles && !file && (
              <>
                <label htmlFor="file-input">
                  <IconPaperclip
                    size={18}
                    role="button"
                    color="var(--ion-color-step-600)"
                    style={{ marginRight: "0.8rem" }}
                  />
                </label>
                <input
                  id="file-input"
                  name="file-input"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  className="display-none"
                  onChange={handleFileChange}
                />
              </>
            )}
            {variables &&
              variables.map((variable) => (
                <button
                  key={variable}
                  className={styles.variableBtn}
                  onClick={() => handleInsertText(`{{${variable}}}`)}
                >
                  {`{{${variable}}}`}
                </button>
              ))}
          </div>
          <span className={`${styles.maxLength} fs-2`}>
            {`${text.length || 0} / ${maxLength}`}
          </span>
        </div>
      )}
      {file && setFile && (
        <div className="w-100 flex align-items-center gap-2">
          {file.type.includes("image") ? (
            <IconPhoto size={19} color="var(--ion-color-step-600)" />
          ) : (
            <IconFile size={19} color="var(--ion-color-step-600)" />
          )}
          <p className="mb-0 fs-2">{file.name}</p>
          <IconX
            size={17}
            role="button"
            color="var(--ion-color-step-600)"
            onClick={() => setFile(null)}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateInput;
